import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  type SelectTriggerVariant,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { REMINDER_PRESET_MINUTES } from "@/shared/reminderPresets.ts";
import type { ReminderOverride } from "@/types/artifacts.ts";
import { Bell, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Shared with native so the two event editors cannot drift (native/src/features/calendar/sheets/EventRemindersSection.tsx).
const PRESET_MINUTES = REMINDER_PRESET_MINUTES;

/** Display label for a preset minutes value. */
function formatPreset(minutes: number): string {
  if (minutes === 0) return "At start";
  if (minutes < 60) return `${minutes} mins`;
  if (minutes === 60) return "1 hr";
  if (minutes < 1440) return `${minutes / 60} hrs`;
  if (minutes === 1440) return "1 day";
  return `${minutes} mins`;
}

interface RemindersSectionProps {
  value: ReminderOverride[] | undefined;
  onChange: (next: ReminderOverride[]) => void;
  /** When true, the UI renders read-only. */
  disabled?: boolean;
  /**
   * When true, the server hasn't confirmed the event's reminder state yet
   * (e.g. the local EventSource pre-dates the Phase 4 inbound backfill).
   * Renders a spinner and blocks all edits — we don't know what reminders
   * the provider has, so letting the user add one here could clobber them.
   */
  loading?: boolean;
  /** Optional muted hint line rendered below the list (e.g., Outlook single-reminder warning). */
  hint?: string;
  /** SelectTrigger styling variant — the sidebar opts into the `editable` hover affordance. */
  triggerVariant?: SelectTriggerVariant;
}

/**
 * Shared reminders editor for the event edit sidebar and the invitation artifact view.
 *
 * Stateless relative to its caller: every edit, add, and remove invokes `onChange`
 * with a fresh array. Callers decide whether to batch changes (sidebar form) or
 * persist on each change (invitation artifact `updateArtifact`).
 */
export function RemindersSection({
  value,
  onChange,
  disabled = false,
  loading = false,
  hint,
  triggerVariant,
}: RemindersSectionProps) {
  const reminders = value ?? [];
  const hasReminders = reminders.length > 0;

  // Each row needs a stable key that survives non-tail deletions — index-based
  // keys shift indices under remaining rows and remount them, losing local
  // state (isCustom, customDraft). Track a parallel array of unique ids.
  const idCounter = useRef(0);
  const nextId = () => {
    idCounter.current += 1;
    return `r-${idCounter.current}`;
  };
  const [ids, setIds] = useState<string[]>(() => reminders.map(nextId));

  // When the parent re-seeds the value externally (initial fetch, backfill
  // arrives, recalled from server) the length changes without our handlers
  // running. Regenerate ids in that case — we can't match old rows to new
  // ones, so treat it as a fresh list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: regenerate only on length change, not on every value identity tick.
  useEffect(() => {
    if (ids.length !== reminders.length) {
      setIds(reminders.map(nextId));
    }
  }, [reminders.length]);

  const addReminder = () => {
    setIds((prev) => [...prev, nextId()]);
    onChange([...reminders, { method: "popup", minutes: 10 }]);
  };

  const updateReminder = (index: number, next: ReminderOverride) => {
    onChange(reminders.map((r, i) => (i === index ? next : r)));
  };

  const removeReminder = (index: number) => {
    setIds((prev) => prev.filter((_, i) => i !== index));
    onChange(reminders.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <output
        className="flex items-center gap-3"
        aria-busy="true"
        aria-label="Syncing reminders from the calendar provider"
      >
        <Bell className="size-4 text-muted-foreground shrink-0" />
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          <span>Syncing reminders…</span>
        </div>
      </output>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Bell className="size-4 text-muted-foreground shrink-0 mt-1.5" />
        </TooltipTrigger>
        <TooltipContent side="left">Reminders</TooltipContent>
      </Tooltip>
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {hasReminders ? (
          <>
            {reminders.map((reminder, index) => (
              <ReminderRow
                key={ids[index] ?? `idx-${index}`}
                reminder={reminder}
                disabled={disabled}
                onChange={(next) => updateReminder(index, next)}
                onRemove={() => removeReminder(index)}
                triggerVariant={triggerVariant}
              />
            ))}
            {/* Secondary "+ Add reminder" link to add more once at least one exists. */}
            {!disabled && (
              <Button
                type="button"
                variant="outlinePopover"
                size="action"
                onClick={addReminder}
                className="h-6 w-fit text-xs px-2 py-1 hover:bg-accent hover:text-accent-foreground hover:border-accent"
                data-statsig-name="event-add-reminder"
              >
                + Add
              </Button>
            )}
            {hint && <p className="text-xs text-muted-foreground italic">{hint}</p>}
          </>
        ) : disabled ? (
          <span className="text-sm text-muted-foreground">No reminders</span>
        ) : (
          // Empty + editable: standard add button matching the other sections.
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={addReminder}
            className="w-fit"
            data-statsig-name="event-add-reminder"
          >
            Add reminder
          </Button>
        )}
      </div>
    </div>
  );
}

function ReminderRow({
  reminder,
  disabled,
  onChange,
  onRemove,
  triggerVariant,
}: {
  reminder: ReminderOverride;
  disabled: boolean;
  onChange: (next: ReminderOverride) => void;
  onRemove: () => void;
  triggerVariant?: SelectTriggerVariant;
}) {
  const matchesPreset = (PRESET_MINUTES as readonly number[]).includes(reminder.minutes);
  const [isCustom, setIsCustom] = useState(!matchesPreset);
  const [customDraft, setCustomDraft] = useState(String(reminder.minutes));

  const handleMinutesChange = (raw: string) => {
    if (raw === "custom") {
      setIsCustom(true);
      setCustomDraft(String(reminder.minutes));
      return;
    }
    const minutes = Number(raw);
    if (Number.isFinite(minutes)) {
      setIsCustom(false);
      onChange({ ...reminder, minutes });
    }
  };

  const commitCustom = () => {
    const parsed = Number(customDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      // Revert to current value if invalid
      setCustomDraft(String(reminder.minutes));
      return;
    }
    const clamped = Math.max(0, Math.min(40320, Math.floor(parsed)));
    onChange({ ...reminder, minutes: clamped });
  };

  return (
    <div className="flex items-center gap-1.5">
      {isCustom ? (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={0}
            max={40320}
            value={customDraft}
            disabled={disabled}
            onChange={(e) => setCustomDraft(e.target.value)}
            onBlur={commitCustom}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
              }
            }}
            className={cn("h-7 w-20 text-sm", "px-2")}
            aria-label="Minutes before event"
          />
          <span className="text-sm text-muted-foreground whitespace-nowrap">mins</span>
        </div>
      ) : (
        <Select
          value={String(reminder.minutes)}
          onValueChange={handleMinutesChange}
          disabled={disabled}
        >
          <SelectTrigger
            variant={triggerVariant}
            className="h-7 text-sm w-auto min-w-[9rem]"
            aria-label="Reminder time"
          >
            <SelectValue>{formatPreset(reminder.minutes)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {PRESET_MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {formatPreset(m)}
              </SelectItem>
            ))}
            <SelectItem value="custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
      )}

      <Select
        value={reminder.method}
        onValueChange={(method) => {
          if (method === "popup" || method === "email") {
            onChange({ ...reminder, method });
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger
          variant={triggerVariant}
          className="h-7 text-sm w-auto min-w-[5.5rem]"
          aria-label="Reminder method"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="popup">Popup</SelectItem>
          <SelectItem value="email">Email</SelectItem>
        </SelectContent>
      </Select>

      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="size-6 shrink-0 text-muted-foreground"
          aria-label="Remove reminder"
          data-statsig-name="event-remove-reminder"
        >
          <X className="size-3.5" />
        </Button>
      )}
    </div>
  );
}
